import {
  FLOW_TOF_RPC_TIMEOUT_MS,
  type RobotCommandResult,
} from '@/connection/BatchRobotService';
import { abortableDelay } from '../../abortableDelay';
import type { Logger, NodeDefinition } from '../../types';
import { t, r } from '../../rubyParts';
import { TofNodeView } from '../views/TofNode';

const TOF_MODE = 1 as const;
const TOF_AREA = 0x16 as const;
const TOF_SAMPLE_INTERVAL_MS = 1000;
const TOF_TIMEOUT_MS = 3000;
let tofRequestId = 0;

const logResults = (log: Logger, results: RobotCommandResult[]) => {
  results.forEach((result) => {
    if (result.status !== 'fulfilled') {
      log(`[TOF][${result.robotId}] ${result.status}: ${result.message ?? result.errorCode ?? '不明なエラー'}`);
    }
  });
};

export const tofDefinition: NodeDefinition<'tof'> = {
  label: 'TOF',
  showInPalette: true,
  paletteColor: 'bg-red-100 border-red-500 text-red-700',
  miniMapColor: '#db1009',
  defaultData: { label: 'TOF' },
  view: TofNodeView,
  run: async (node, ctx, log) => {
    const mode = TOF_MODE;
    const area = TOF_AREA;
    const requestId = ++tofRequestId;
    ctx.setTofScan?.({ mode, area, requestId, value: 0, sampleIndex: 0 });

    try {
      if (!ctx.robotService) {
        if (ctx.measureSimulationTof) {
          const measuredValue = Math.max(0, Math.round(ctx.measureSimulationTof()));
          ctx.lastValue = measuredValue;
          ctx.onExecutionEvent?.({ type: 'tof', nodeId: node.id, distanceMm: measuredValue });
          ctx.setTofScan?.({ mode, area, requestId, value: measuredValue, sampleIndex: 1 });
          const delayResult = await abortableDelay(TOF_SAMPLE_INTERVAL_MS, ctx.signal);
          if (delayResult !== 'completed') return;
          log(measuredValue > 0
            ? `[TOF][Simulation] ${measuredValue} mm を取得しました`
            : '[TOF][Simulation] 検知対象がないため 0 mm を返しました');
          return;
        }
        ctx.lastValue = 0;
        const startedAt = performance.now();
        let sampleIndex = 0;
        while (performance.now() - startedAt < TOF_TIMEOUT_MS) {
          const remainingMs = TOF_TIMEOUT_MS - (performance.now() - startedAt);
          const delayResult = await abortableDelay(Math.min(TOF_SAMPLE_INTERVAL_MS, remainingMs), ctx.signal);
          if (delayResult !== 'completed') return;
          sampleIndex += 1;
          ctx.setTofScan?.({ mode, area, requestId, value: 0, sampleIndex });
        }
        log('[TOF][Simulation] 3秒以内に距離を取得できなかったため 0 mm を返しました');
        ctx.onExecutionEvent?.({ type: 'tof', nodeId: node.id, distanceMm: 0 });
        return;
      }
      if (!ctx.robotService.measureTOF) {
        log('[TOF] エラー: TOF センサーが利用できません');
        return { nextHandle: null };
      }

      const summary = await ctx.robotService.measureTOF(
        { mode, area },
        { nodeId: node.id, signal: ctx.signal, timeoutMs: FLOW_TOF_RPC_TIMEOUT_MS },
      );
      logResults(log, summary.results);
      const positiveResult = summary.results.find((result) => (
        result.status === 'fulfilled'
        && typeof result.value === 'number'
        && Number.isFinite(result.value)
        && result.value > 0
      ));
      const positiveValue = positiveResult?.status === 'fulfilled' && typeof positiveResult.value === 'number'
        ? positiveResult.value
        : 0;
      ctx.setTofScan?.({ mode, area, requestId, value: positiveValue, sampleIndex: 1 });
      if (positiveValue > 0) {
        ctx.lastValue = positiveValue;
        ctx.onExecutionEvent?.({ type: 'tof', nodeId: node.id, distanceMm: positiveValue });
        log(`[TOF] ${positiveValue} mm を取得しました`);
        return;
      }

      ctx.lastValue = 0;
      ctx.onExecutionEvent?.({ type: 'tof', nodeId: node.id, distanceMm: 0 });
      log('[TOF] 3秒以内に距離を取得できなかったため 0 mm を返しました');
    } finally {
      ctx.setTofScan?.(null);
    }
  },
  help: {
    title: [t('TOF（'), r('距離', 'きょり'), t('センサー）')],
    description: [
      r('距離', 'きょり'),
      t('センサーで、ものまでの'),
      r('距離', 'きょり'),
      t('を'),
      r('測', 'はか'),
      t('ります。'),
      r('測', 'はか'),
      t('った'),
      r('距離', 'きょり'),
      t('（ミリメートル）は、つぎのカードに'),
      r('渡', 'わた'),
      t('されます。'),
      r('何', 'なに'),
      t('も'),
      r('見', 'み'),
      t('つからないときは、0になります。'),
    ],
  },
};
